import type { RoleTemplate, RoleTemplateId, TaDeskCapability, iUserAccess } from "./access.models";
import {
  createOwnerAccess,
  isValidUserAccess,
  isValidUserAccessV2,
  ORGANIZATION_ROLES,
  type StoredUserAccess,
} from "./access.models";
import { getRoleFromPermission } from "@sdk/roles-and-permissions/roles-and-permissions";
import type { iUserAppPermission } from "@sdk/user-management/user-management.models";

export const ROLE_TEMPLATES: Record<RoleTemplateId, RoleTemplate> = {
  owner: {
    id: "owner",
    label: "Owner",
    description: "Full access to all brands and workspace settings",
    org: { isOwner: true, capabilities: [] },
    brandCapabilities: [],
  },
  brand_manager: {
    id: "brand_manager",
    label: "Brand Manager",
    description: "Manage and view team records for assigned brands",
    org: { isOwner: false, capabilities: [] },
    brandCapabilities: [
      "MANAGE_CONTACTS",
      "MANAGE_ENQUIRIES",
      "MANAGE_QUOTES",
      "MANAGE_QUOTE_TEMPLATES",
      "MANAGE_BOOKINGS",
      "VIEW_TEAM_ENQUIRIES",
      "VIEW_TEAM_QUOTES",
      "VIEW_TEAM_BOOKINGS",
    ],
  },
  travel_agent: {
    id: "travel_agent",
    label: "Travel Agent",
    description: "Manage own enquiries, quotes, and bookings on assigned brands",
    org: { isOwner: false, capabilities: [] },
    brandCapabilities: [
      "MANAGE_CONTACTS",
      "MANAGE_ENQUIRIES",
      "MANAGE_QUOTES",
      "MANAGE_BOOKINGS",
    ],
  },
  bookings_team: {
    id: "bookings_team",
    label: "Bookings Team",
    description: "View all bookings and manage financial operations",
    org: { isOwner: false, capabilities: [] },
    brandCapabilities: ["VIEW_ALL_BOOKINGS", "MANAGE_BOOKING_FINANCE"],
  },
  reporter: {
    id: "reporter",
    label: "Reporter",
    description: "View reports across the organization",
    org: { isOwner: false, capabilities: ["VIEW_REPORTS"] },
    brandCapabilities: [],
  },
  accountant: {
    id: "accountant",
    label: "Accountant",
    description: "View and manage accounting across the organization",
    org: { isOwner: false, capabilities: ["VIEW_ACCOUNTING", "MANAGE_ACCOUNTING"] },
    brandCapabilities: [],
  },
  custom: {
    id: "custom",
    label: "Custom",
    description: "Manually configured capabilities",
    org: { isOwner: false, capabilities: [] },
    brandCapabilities: [],
  },
};

export const ROLE_TEMPLATE_LIST = Object.values(ROLE_TEMPLATES).filter(
  (t) => t.id !== "custom"
);

export type OrgRoleId = "none" | "reporter" | "accountant" | "accountant_reporter";

const ORG_ROLE_CAPABILITIES: Record<
  Exclude<OrgRoleId, "none">,
  TaDeskCapability[]
> = {
  reporter: ["VIEW_REPORTS"],
  accountant: ["VIEW_ACCOUNTING", "MANAGE_ACCOUNTING"],
  accountant_reporter: ["VIEW_ACCOUNTING", "MANAGE_ACCOUNTING", "VIEW_REPORTS"],
};

export function detectOrgRole(
  access: Pick<iUserAccess, "org">
): OrgRoleId {
  if (access.org.isOwner) return "none";
  const caps = new Set(access.org.capabilities);
  const hasAccounting = caps.has("VIEW_ACCOUNTING");
  const hasReports = caps.has("VIEW_REPORTS");
  if (hasAccounting && hasReports) return "accountant_reporter";
  if (hasAccounting) return "accountant";
  if (hasReports) return "reporter";
  return "none";
}

export function applyOrgRoleToAccess(
  access: iUserAccess,
  orgRole: OrgRoleId
): iUserAccess {
  if (orgRole === "none") {
    return {
      ...access,
      org: { isOwner: false, capabilities: [] },
      version: 1,
    };
  }
  return {
    ...access,
    org: {
      isOwner: false,
      capabilities: [...ORG_ROLE_CAPABILITIES[orgRole]],
    },
    version: 1,
  };
}

export function getTemplateCapabilities(
  roleTemplate: RoleTemplateId
): TaDeskCapability[] {
  return [...ROLE_TEMPLATES[roleTemplate].brandCapabilities];
}

export function getTemplateOrgCapabilities(
  roleTemplate: RoleTemplateId
): TaDeskCapability[] {
  return [...ROLE_TEMPLATES[roleTemplate].org.capabilities];
}

const ORG_ROLE_LABELS: Record<Exclude<OrgRoleId, "none">, string> = {
  reporter: "Reporter (org-wide)",
  accountant: "Accountant (org-wide)",
  accountant_reporter: "Accountant + Reports (org-wide)",
};

export function formatBrandAccessSummary(
  access: StoredUserAccess,
  brandNameById: Record<string, string> = {}
): string {
  if (access.org.isOwner) {
    return "Owner (all brands)";
  }

  if (isValidUserAccessV2(access)) {
    const roleLabels = access.org.roles.map(
      (role) => ORGANIZATION_ROLES[role]?.label ?? role
    );
    const parts: string[] = [...roleLabels];

    if (access.brandIds.length) {
      parts.push(
        access.brandIds.map((id) => brandNameById[id] ?? id).join(", ")
      );
    }

    if (parts.length === 0) {
      return "No access";
    }

    const orgWideOnly =
      access.brandIds.length === 0 &&
      access.org.roles.length > 0 &&
      access.org.roles.every(
        (role) => role === "reporter" || role === "accountant"
      );

    if (orgWideOnly && access.org.roles.length === 1) {
      return `${roleLabels[0]} (org-wide)`;
    }

    return parts.join(" · ");
  }

  const v1 = access as iUserAccess;
  const parts: string[] = [];
  const orgRole = detectOrgRole(v1);
  if (orgRole !== "none") {
    parts.push(ORG_ROLE_LABELS[orgRole]);
  }

  if (v1.brandAccess.length) {
    parts.push(
      ...v1.brandAccess.map((entry) => {
        const brandLabel = brandNameById[entry.brandId] ?? entry.brandId;
        const roleLabel =
          ROLE_TEMPLATES[entry.roleTemplate]?.label ?? entry.roleTemplate;
        return `${brandLabel}: ${roleLabel}`;
      })
    );
  }

  if (parts.length === 0) {
    return "No access";
  }

  return parts.join(" · ");
}

/** Whether the user is an org owner (access v1 or legacy permissions). */
export function isOrgOwner(user: {
  access?: { version?: number; org?: { isOwner?: boolean } };
  permissions?: iUserAppPermission[];
}): boolean {
  if (user.access?.version === 1 || user.access?.version === 2) {
    return user.access.org?.isOwner === true;
  }
  return getRoleFromPermission(user.permissions ?? []) === "Owner";
}

/** Display label for user access — prefers access v1, falls back to legacy role. */
export function summarizeUserAccess(
  user: {
    access?: StoredUserAccess;
    permissions?: iUserAppPermission[];
  },
  brandNameById: Record<string, string> = {}
): string {
  if (user.access && isValidUserAccess(user.access)) {
    return formatBrandAccessSummary(user.access, brandNameById);
  }
  const legacy = getRoleFromPermission(user.permissions ?? []);
  return legacy === "N/A" ? "No access" : legacy;
}
